import type {
  SurpriseBag,
  SurpriseBagItem,
  SurpriseBagItemTag,
  ItemCategoryDetails,
} from '@pkg/types/surpriseBag';
import { BAG_AVAILABILITY } from '@pkg/types/surpriseBag';
import { substitutePlainTextVariables } from '@pkg/utilities/strings';

/**
 * Formats a price object into a localized currency string
 * @param itemPrice - Price object with code, minorUnits, and decimals
 * @param locale - Locale string for number formatting (e.g., 'en-GB', 'de-DE')
 * @returns Formatted price string (e.g., '£5.99') or '0' if price is undefined
 */
export function getPrice(
  itemPrice: { code: string; minorUnits: number; decimals: 2 } | undefined,
  locale: string,
): string {
  if (itemPrice) {
    const price = itemPrice.minorUnits / Math.pow(10, itemPrice.decimals);
    const formattedPrice = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: itemPrice.code,
    }).format(price);
    return formattedPrice;
  } else {
    return '0';
  }
}

/**
 * Determines the availability status of a surprise bag
 * @param bag - SurpriseBag object to check availability for
 * @returns BAG_AVAILABILITY enum value (AVAILABLE, SOLD_OUT, or UNAVAILABLE)
 */
export function getBagAvailability(bag: SurpriseBag) {
  const hasItems = bag?.itemsAvailable > 0;
  const inSales = bag?.inSalesWindow;
  const hasPickup = !!bag?.pickupInterval;
  if (hasItems && inSales && hasPickup) {
    return BAG_AVAILABILITY.AVAILABLE;
  }

  if (!hasItems && inSales && hasPickup) {
    return BAG_AVAILABILITY.SOLD_OUT;
  }

  return BAG_AVAILABILITY.UNAVAILABLE;
}

export function isBagAvailable(itemsAvailable: number, 
  inSalesWindow: boolean, 
  pickupInterval: boolean ) {
  const hasItems = itemsAvailable > 0;
  const inSales = inSalesWindow;
  const hasPickup = !!pickupInterval;
  if (hasItems && inSales && hasPickup) {
    return true;
  }

  if (!hasItems && inSales && hasPickup) {
    return false;
  }

  return false;
}

export function getRatingSubtitle(ratingSummary?: string, ratingCount?: number, ratingMonths?: number) {
  const summary = ratingSummary ?? '';
  const countText = ratingCount !== undefined ? String(ratingCount) : '';
  const monthsText = ratingMonths !== undefined ? String(ratingMonths) : '';
  return substitutePlainTextVariables(summary, { rating: countText, months: monthsText });
}

const UNAVAILABLE_TAG_VARIANTS = new Set<string>([
  'SOLD_OUT',
  'NOTHING_TO_SAVE_TODAY',
  'CHECK_AGAIN_LATER',
]);

export function getUnavailableLabel(
  itemTags: SurpriseBagItemTag[] | null | undefined,
): string {
  if (!Array.isArray(itemTags)) {
    return '';
  }

  for (const tag of itemTags) {
    if (UNAVAILABLE_TAG_VARIANTS.has(tag?.variant ?? '')) {
      const label = tag?.shortText?.trim();
      if (label) {
        return label;
      }
    }
  }

  return '';
}

export type ItemTagBadgeVariant =
  | 'base'
  | 'attention'
  | 'negative'
  | 'positive'
  | 'unavailable';

export interface ItemTagBadge {
  /** Stable `v-for` key; the API can repeat the `GENERIC` id across variants. */
  key: string;
  /** Already localized by the API. */
  label: string;
  variant: ItemTagBadgeVariant;
  /** The API's own variant, e.g. `ENDING_SOON`, which drives the pop up illustration. */
  tagVariant: string;
  description?: string;
  descriptionHeading?: string;
  /** Only kept when it adds something the badge label does not already say. */
  longText?: string;
}

/**
 * Badge styling per API tag variant, keyed on `variant` rather than `id`
 * because every merchandising tag shares the `GENERIC` id.
 *
 * Only the non-`base` variants are listed. Popular, Hidden gem, Rare find,
 * Plan ahead, Vegetarian, Vegan, Dine-in, Selling fast, Reserve ahead and New
 * are all `base`, which is also what an unrecognised variant falls back to, so
 * a tag the API adds later renders in the majority style instead of vanishing.
 */
const ITEM_TAG_BADGE_VARIANTS: Record<string, ItemTagBadgeVariant> = {
  /** `attention` is already the subtle attention surface. */
  X_ITEMS_LEFT: 'attention',
  SOLD_OUT: 'unavailable',
  /** Rendered as "Sold out today". */
  NOTHING_TO_SAVE_TODAY: 'unavailable',
  CHECK_AGAIN_LATER: 'unavailable',
  LOCAL_HERO: 'positive',
  ENDING_SOON: 'negative',
};

/** Maps an API tag variant onto a `BadgeLabel` variant. */
export function getItemTagBadgeVariant(variant: string): ItemTagBadgeVariant {
  return ITEM_TAG_BADGE_VARIANTS[variant] ?? 'base';
}

/**
 * Turns the API's `itemTags` into badge props for the bag image overlay,
 * preserving the order the API returns them in, which is its priority order.
 * Tags with no `shortText` are dropped rather than rendered as an empty badge.
 */
export function getItemTagBadges(
  itemTags?: SurpriseBagItemTag[] | null,
): ItemTagBadge[] {
  if (!Array.isArray(itemTags)) {
    return [];
  }

  return itemTags.reduce<ItemTagBadge[]>((badges, tag, index) => {
    const label = tag?.shortText?.trim();
    if (!label) {
      return badges;
    }

    const description = tag?.description?.trim();
    const descriptionHeading = tag?.descriptionHeading?.trim();
    const longText = tag?.longText?.trim();

    badges.push({
      key: `${tag.id ?? 'tag'}-${tag.variant ?? index}`,
      label,
      variant: getItemTagBadgeVariant(tag.variant ?? ''),
      tagVariant: tag.variant ?? '',
      ...(description ? { description } : {}),
      ...(descriptionHeading ? { descriptionHeading } : {}),
      ...(longText && longText !== label ? { longText } : {}),
    });

    return badges;
  }, []);
}

export function getPickupTag (pickUpIntervalStart: string, isoCode: string ) {
  
  try {
    const surpriseBagDate = new Date(pickUpIntervalStart);
    if (Number.isNaN(surpriseBagDate.getTime())) {
      return '';
    }

    const locale = isoCode || undefined;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const getLocalDateKey = (date: Date) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date);
      } catch {
        return date.toDateString();
      }
    };

    const targetKey = getLocalDateKey(surpriseBagDate);
    if (getLocalDateKey(today) === targetKey) {
      return 'today';
    }
    if (getLocalDateKey(tomorrow) === targetKey) {
      return 'tomorrow';
    }
    return '';
  } catch {
    return '';
  }

}

export function getCollectionWindowLabel(pickUpIntervalStart: string, pickupIntervalEnd: string, staticLabel: string) {
  const startDate = new Date(pickUpIntervalStart);
  const endDate = new Date(pickupIntervalEnd);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return undefined;
  }

  const pickupWindowStartTime = getTime(startDate);
  const pickupWindowEndTime = getTime(endDate);
  const pickupLabel = `${staticLabel || ''}${staticLabel ? ': ' : ''}`;

  return {
    pickupWindowLabel: pickupLabel,
    pickupWindowStartTime: pickupWindowStartTime,
    pickupWindowEndTime: pickupWindowEndTime
  };
}

/**
 * Helper function to format time from a Date object
 * @param date - Date object to format
 * @returns Formatted time string (HH:MM)
 */
function getTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export const getTitle = (storeName?: string, branch?: string) => {
  if (storeName && branch) {
    return `${storeName} - ${branch}`;
  } else if (storeName && !branch) {
    return storeName;
  }
  return undefined;
};

/** Prefer localized V2 category name when the SEO API provides it. */
export function getItemCategoryLabel(
  item?: Pick<SurpriseBagItem, 'itemCategory' | 'itemCategoryV2Details'> | null,
): string {
  const name = item?.itemCategoryV2Details?.name?.trim();
  return name || item?.itemCategory || '';
}


export function getItemSubCategoryDetails(
  item?: Pick<SurpriseBagItem, 'itemSubCategoryDetails'> | null,
): ItemCategoryDetails | null {
  return normalizeCategoryDetails(item?.itemSubCategoryDetails);
}

/**
 * Same as `getItemSubCategoryDetails` for the broader V2 category, which is the
 * fallback when a bag has no subcategory details.
 */
export function getItemCategoryV2Details(
  item?: Pick<SurpriseBagItem, 'itemCategoryV2Details'> | null,
): ItemCategoryDetails | null {
  return normalizeCategoryDetails(item?.itemCategoryV2Details);
}

function normalizeCategoryDetails(
  details?: ItemCategoryDetails | null,
): ItemCategoryDetails | null {
  const name = details?.name?.trim();
  return details && name ? { ...details, name } : null;
}